'use client'

import type React from 'react'

import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { FlexColumn } from 'src/components/Atoms/FlexBox'
import { Close } from 'src/components/Molecules/Close'

interface DrawerContentProps {
  onClose: () => void
  children?: React.ReactNode
  title: string
  showFooter?: boolean
  noPadding?: boolean
  footerContent?: React.ReactNode
};

const DrawerContentHeader = styled(FlexColumn)(({ theme }) => ({
  padding: theme.sizes.spacing['08'],
  position: 'relative',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  width: '100%',
  backgroundColor: theme.colors.surface.primary,
  borderBottom: `1px solid ${theme.colors.border.secondary}`,
}))

const DrawerContentContent = styled(FlexColumn)(({ theme }) => ({
  padding: theme.sizes.spacing['08'],
  gap: theme.sizes.spacing['06'],
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  height: '100%',
  width: '100%',
  overflowY: 'auto',
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.colors.surface.tertiary} transparent`,
  '&::-webkit-scrollbar': {
    display: 'block',
    width: '0.375rem',
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: theme.sizes.radius.full,
  },
}))

const DrawerContentFooter = styled(FlexColumn)(({ theme }) => ({
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  alignSelf: 'stretch',
  width: '100%',
  padding: `${theme.sizes.spacing['07']} ${theme.sizes.spacing['07']} ${theme.sizes.spacing['07']} ${theme.sizes.spacing['09']}`,
  backgroundColor: theme.colors.surface.secondary,
  borderBottom: `1px solid ${theme.colors.border.secondary}`,
}))

const CloseWrapper = styled(FlexColumn)(({ theme }) => ({
  position: 'absolute',
  top: theme.sizes.spacing['04'],
  right: theme.sizes.spacing['04'],
  zIndex: 1000,
  color: theme.colors.text.primary,
  backgroundColor: theme.colors.surface.primary,
  '&:hover': {
    backgroundColor: theme.colors.surface.secondary,
  },
}))

const DrawerContent: React.FC<DrawerContentProps> = ({
  title,
  children,
  onClose,
  showFooter = true,
  noPadding = false,
  footerContent,
}) => (
  <>
    <DrawerContentHeader>
      <Typography variant='heading-lg'>{title}</Typography>
      <CloseWrapper>
        <Close icon onClick={() => onClose()} />
      </CloseWrapper>
    </DrawerContentHeader>
    <DrawerContentContent sx={{
      padding: noPadding ? 0 : undefined,
    }}
    >
      {children}
    </DrawerContentContent>
    {showFooter && (
      <DrawerContentFooter>
        {footerContent ?? <Close variant='text' onClick={() => onClose()} />}
      </DrawerContentFooter>
    )}
  </>
)

export default DrawerContent
export type { DrawerContentProps }
